<template>
  <div class="amap-container">
    <!-- 地图容器 -->
    <div class="map-wrapper">
      <div id="tdtMap" class="map-container"></div>
    </div>

    <!-- 控制面板 - 编辑模式区分 -->
    <div class="control-panel" v-if="validPondId">
      <!-- 编辑模式：有原有区域显示删除/重置，无则显示开始框选 -->
      <template v-if="isEditMode">
        <button
          @click="deleteExistingArea"
          class="control-btn delete-btn"
          v-if="hasExistingArea"
        >
          删除原有区域
        </button>
        <button
          @click="resetEditDrawing"
          class="control-btn clear-btn"
          :disabled="!canResetEditDrawing"
        >
          重置框选区域
        </button>
      </template>

      <button
        @click="toggleDrawingMode"
        :class="['control-btn', { active: isDrawingMode }]"
        v-if="!hasExistingArea || (isEditMode && !hasExistingArea)"
      >
        {{ isDrawingMode ? "退出绘制" : "开始框选" }}
      </button>

      <button
        @click="clearDrawing"
        class="control-btn clear-btn"
        :disabled="!hasDrawingData"
        v-if="!isEditMode"
      >
        清除
      </button>
      <button
        @click="handleSaveArea"
        class="control-btn save-btn"
        :disabled="!hasValidArea || !validPondId || isAreaOverlapping"
      >
        保存区域经纬度
      </button>
    </div>

    <div v-if="isDrawingMode" class="status-tip drawing">
      ✏️ 绘制模式 - 点击地图添加顶点 (已添加: {{ drawingPoints.length }}个)
      <br />
      <small>提示: 至少需要3个点才能完成绘制</small>
      <br />
      <small>提示: 绘制完成后右键退出绘制</small>
    </div>

    <div
      v-if="isAreaOverlapping"
      class="status-tip error"
      :class="{ 'has-zoom-tip': !showFullArea }"
    >
      ⚠️ 当前框选区域与已有虾塘区域重叠，请重新框选！
    </div>

    <div v-if="!showFullArea" class="zoom-tip">
      🔍 当前缩放级别较小，显示虾塘中心点。放大到16级及以上可查看完整区域。
    </div>

    <div v-if="areaCoords.length" class="area-info">
      <h3>📍 区域信息</h3>
      <div class="info-section">
        <p><strong>顶点数量:</strong> {{ areaCoords.length }} 个</p>
        <p>
          <strong>区域面积:</strong>
          {{ areaArea ? areaArea.toFixed(2) : "计算中..." }} 平方米
        </p>

        <div v-if="centerPoint" class="center-point">
          <p><strong>中心点坐标:</strong></p>
          <p class="coord">经度: {{ centerPoint.lng.toFixed(6) }}</p>
          <p class="coord">纬度: {{ centerPoint.lat.toFixed(6) }}</p>
        </div>

        <div class="coordinates-preview">
          <strong>边界坐标:</strong>
          <div class="coords-list">
            <div
              v-for="(point, index) in areaCoords"
              :key="index"
              class="coord-item"
            >
              {{ index + 1 }}. ({{ point.lat.toFixed(6) }},
              {{ point.lng.toFixed(6) }})
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 当前鼠标位置信息 -->
    <div v-if="mousePosition && isDrawingMode" class="mouse-position">
      <strong>鼠标位置:</strong>
      {{ mousePosition.lat.toFixed(6) }}, {{ mousePosition.lng.toFixed(6) }}
      <span class="drawing-hint">- 点击添加顶点</span>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import {
  deletePondCoordinatesById,
  getAllCentralCoordinate,
  getPondAddressDetailByCentral,
  getPondCoordinatesById,
  getShrimpPondById,
  savePondCoordinates,
} from "@/api/pond";

// 天地图密钥配置
const TDT_KEY = "0f307953089784b73c9d76e2605aa363";

export default {
  name: "PondMap",
  props: {
    pondId: {
      type: [String, Number, null, undefined],
      default: null,
    },
    // 弹窗显示状态
    visible: {
      type: Boolean,
      default: true,
    },
    refreshFlag: {
      type: Number,
      default: 0,
    },
    // 是否为独立页面模式
    isStandalone: {
      type: Boolean,
      default: false,
    },
    // 接收初始中心点
    initCenter: {
      type: Object,
      default: () => ({ lng: 113.3245, lat: 23.1061 }),
    },
    // 编辑模式标记
    isEditMode: {
      type: Boolean,
      default: false,
    },
    // 是否已有区域（编辑模式下）
    hasExistingArea: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["save-success", "save-fail", "close", "delete-success"],
  setup: function (props, { emit }) {
    // 地图基础配置
    const mapCenter = ref([props.initCenter.lng, props.initCenter.lat]);
    const zoomLevel = ref(15);
    const enableScrollWheel = ref(true);

    // 地图与绘制状态
    let mapInstance = null;
    let isMapInitializing = false; // 防止重复初始化
    let currentPolygon = null; // 当前绘制的多边形
    let tempPolyline = null; // 临时线
    let markers = []; // 绘制的点标记
    let existingPolygons = [];
    const isDrawingMode = ref(false);
    const drawingPoints = ref([]); // 绘制中的点
    const areaCoords = ref([]); // 最终的区域坐标
    const areaArea = ref(null);
    const centerPoint = ref(null);
    const mousePosition = ref(null);
    const existingPonds = ref([]);
    const isAreaOverlapping = ref(false); // 标记当前区域是否重叠
    const showFullArea = ref(false); // 初始为false（缩放15）

    // 标记是否有新的绘制内容（区别于原有区域）
    const isNewDrawing = ref(false);

    // 计算属性
    const hasDrawingData = computed(
      () => drawingPoints.value.length > 0 || areaCoords.value.length > 0
    );
    const hasValidArea = computed(() => areaCoords.value.length >= 3);

    // 编辑模式下重置按钮的启用条件（仅当有新绘制内容时可点击）
    const canResetEditDrawing = computed(() => {
      return props.isEditMode && isNewDrawing.value;
    });

    // 编辑模式相关状态
    const editModeArea = ref([]); // 编辑模式下原有区域坐标
    const isEditAreaLoaded = ref(false); // 编辑区域是否已加载

    const hasExistingArea = computed(() => {
      return props.isEditMode && editModeArea.value.length > 0;
    });

    const validPondId = computed(() => {
      return (
        props.pondId !== null &&
        props.pondId !== undefined &&
        props.pondId !== ""
      );
    });

    // 清除所有地图元素
    const clearAllMapElements = () => {
      // 清除标记
      markers.forEach((marker) => {
        try {
          mapInstance.removeOverLay(marker);
        } catch (e) {
          console.warn("移除标记失败:", e);
        }
      });
      markers = [];

      // 清除现有虾塘多边形
      existingPolygons.forEach((polygon) => {
        try {
          mapInstance.removeOverLay(polygon);
        } catch (e) {
          console.warn("移除现有多边形失败:", e);
        }
      });
      existingPolygons = [];

      // 清除当前绘制的多边形和临时线
      if (currentPolygon) {
        try {
          mapInstance.removeOverLay(currentPolygon);
        } catch (e) {
          console.warn("移除当前多边形失败:", e);
        }
        currentPolygon = null;
      }

      if (tempPolyline) {
        try {
          mapInstance.removeOverLay(tempPolyline);
        } catch (e) {
          console.warn("移除临时线失败:", e);
        }
        tempPolyline = null;
      }
    };

    // 动态加载天地图脚本
    const loadTdtScript = () => {
      return new Promise((resolve, reject) => {
        if (window.T) {
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src = `https://api.tianditu.gov.cn/api?v=4.0&tk=${TDT_KEY}`;
        script.type = "text/javascript";

        script.onload = () => {
          console.log("天地图API加载成功");
          setTimeout(resolve, 500);
        };

        script.onerror = (error) => {
          console.error("天地图API加载失败:", error);
          reject(new Error("天地图API加载失败，请检查网络"));
        };

        document.head.appendChild(script);
      });
    };

    // 设置鼠标事件
    const setupMouseEvents = () => {
      if (!mapInstance) return;

      // 鼠标移动事件
      mapInstance.addEventListener("mousemove", (e) => {
        if (!e || !e.lnglat) return;

        // 更新鼠标位置
        mousePosition.value = {
          lat: parseFloat(e.lnglat.getLat().toFixed(6)),
          lng: parseFloat(e.lnglat.getLng().toFixed(6)),
        };

        // 在绘制模式下，更新临时线
        if (isDrawingMode.value && drawingPoints.value.length > 0) {
          updateTempPolyline(e.lnglat);
        }
      });

      // 鼠标移出事件
      mapInstance.addEventListener("mouseout", () => {
        mousePosition.value = null;
      });
    };

    // 绑定地图事件
    const bindMapEvents = () => {
      if (!mapInstance) return;

      // 缩放事件
      mapInstance.addEventListener("zoomend", () => {
        zoomLevel.value = mapInstance.getZoom();
      });

      // 移动事件
      mapInstance.addEventListener("moveend", () => {
        const center = mapInstance.getCenter();
        mapCenter.value = [center.getLng(), center.getLat()];
      });

      // 点击事件（添加顶点）- 只有在有虾塘ID且处于绘制模式时才响应
      mapInstance.addEventListener("click", (e) => {
        if (!validPondId.value || !isDrawingMode.value) return;

        // 天地图事件对象可能包装了原生事件
        const lnglat = e.lnglat || (e.event && e.event.lnglat);
        if (!lnglat) return;

        const point = {
          lng: parseFloat(lnglat.getLng().toFixed(6)),
          lat: parseFloat(lnglat.getLat().toFixed(6)),
        };

        drawingPoints.value.push(point);
        addMarker(lnglat); // 添加点标记
      });

      // 右键事件（完成绘制）- 只有在有虾塘ID且处于绘制模式时才响应
      mapInstance.addEventListener("contextmenu", (e) => {
        if (!validPondId.value || !isDrawingMode.value) return;

        // 阻止默认右键菜单
        if (e && e.preventDefault) {
          try {
            e.preventDefault();
          } catch (err) {
            // 天地图事件可能没有preventDefault方法
            console.warn("无法阻止右键默认行为:", err);
          }
        }

        if (drawingPoints.value.length >= 3) {
          completeDrawing();
        }
      });
    };

    // 添加点标记
    const addMarker = (lnglat) => {
      try {
        // 使用自定义点标记样式，匹配Leaflet版本
        const markerIcon = new window.T.Icon({
          iconUrl:
            "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxMiAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iNiIgY3k9IjYiIHI9IjYiIGZpbGw9IiMxODkwZmYiLz4KPGNpcmNsZSBjeD0iNiIgY3k9IjYiIHI9IjQiIGZpbGw9IndoaXRlIi8+Cjwvc3ZnPgo=",
          iconSize: new window.T.Point(12, 12),
          iconAnchor: new window.T.Point(6, 6),
        });

        const marker = new window.T.Marker(lnglat, { icon: markerIcon });
        marker.addTo(mapInstance);
        markers.push(marker);
      } catch (error) {
        console.error("添加标记失败:", error);
      }
    };

    // 更新临时线
    const updateTempPolyline = (mouseLatLng) => {
      // 清除之前的临时线
      if (tempPolyline) {
        try {
          mapInstance.removeOverLay(tempPolyline);
        } catch (e) {
          console.warn("移除临时线失败:", e);
        }
        tempPolyline = null;
      }

      if (drawingPoints.value.length === 0 || !mouseLatLng) return;

      try {
        // 创建点数组：已添加的点 + 当前鼠标位置
        const points = drawingPoints.value.map(
          (p) => new window.T.LngLat(p.lng, p.lat)
        );

        // 添加鼠标当前位置
        points.push(mouseLatLng);

        // 创建临时线
        tempPolyline = new window.T.Polyline(points, {
          color: "#1890ff",
          weight: 2,
          dashArray: "5,10",
        });
        mapInstance.addOverLay(tempPolyline);
      } catch (error) {
        console.error("绘制临时线失败:", error);
      }
    };

    // 获取所有已有虾塘区域
    const fetchExistingPonds = async () => {
      try {
        // 1. 清除旧数据
        existingPolygons.forEach((element) => {
          try {
            if (element._cleanup) element._cleanup();
            mapInstance.removeOverLay(element);
            if (element._nameLabel && element._nameLabel.parentNode) {
              element._nameLabel.parentNode.removeChild(element._nameLabel);
            }
            if (element._tooltip && element._tooltip.parentNode) {
              element._tooltip.parentNode.removeChild(element._tooltip);
            }
          } catch (e) {
            console.warn("移除现有元素失败:", e);
          }
        });
        existingPolygons = [];
        existingPonds.value = [];

        const centralResult = await getAllCentralCoordinate();

        if (centralResult.code === 0 && centralResult.data?.length) {
          const pondDataList = await Promise.all(
            centralResult.data.map(async (item, i) => {
              let parsedItem = item;
              // 解析字符串格式的中心点
              if (typeof item === "string") {
                try {
                  parsedItem = JSON.parse(item);
                } catch (e) {
                  console.error(`解析虾塘中心点[${i}]失败:`, e);
                  return null;
                }
              }

              // 校验中心点坐标有效性
              const lng = parseFloat(parsedItem.lng);
              const lat = parseFloat(parsedItem.lat);
              if (isNaN(lng) || isNaN(lat)) {
                console.warn(`虾塘中心点[${i}]坐标无效，跳过:`, parsedItem);
                return null;
              }

              // 初始化变量
              let coordinates = [];
              let pondIdFromDetail = "";
              let pondInfo = {
                pondName: "",
                userName: "",
                pondArea: 0.0,
                pondDepth: 0.0,
                address: "",
              };

              // 获取虾塘详情
              try {
                const centralStr = JSON.stringify({
                  lng: parsedItem.lng,
                  lat: parsedItem.lat,
                });
                const detailResult = await getPondAddressDetailByCentral(
                  centralStr
                );
                if (detailResult.code === 0 && detailResult.data) {
                  pondIdFromDetail = detailResult.data.pondId || "";
                  const coordData = detailResult.data.coordinates;
                  coordinates =
                    typeof coordData === "string"
                      ? JSON.parse(coordData)
                      : Array.isArray(coordData)
                      ? coordData
                      : [];
                }
              } catch (e) {
                console.warn(`获取虾塘[${i}]区域失败，使用默认三角形:`, e);
                coordinates = [
                  { lng: lng + 0.0001, lat: lat + 0.0001 },
                  { lng: lng - 0.0001, lat: lat + 0.0001 },
                  { lng: lng, lat: lat - 0.0001 },
                ];
              }

              // 获取完整虾塘信息
              if (pondIdFromDetail) {
                try {
                  const pondDetail = await getShrimpPondById(pondIdFromDetail);
                  if (pondDetail.code === 0 && pondDetail.data) {
                    pondInfo.pondName =
                      pondDetail.data.pondName || `虾塘${pondIdFromDetail}`;
                    pondInfo.userName =
                      pondDetail.data.userName || "未知负责人";
                    pondInfo.pondArea = pondDetail.data.pondArea || 0.0;
                    pondInfo.pondDepth = pondDetail.data.pondDepth || 0.0;
                    const addressVO = pondDetail.data.addressBookVO || {};
                    pondInfo.address =
                      `${addressVO.provinceName || ""}${
                        addressVO.cityName || ""
                      }${addressVO.districtName || ""}${
                        addressVO.detailAddress || ""
                      }` || "未知地址";
                  }
                } catch (e) {
                  console.warn(`获取虾塘[${pondIdFromDetail}]信息失败:`, e);
                  pondInfo.pondName = `虾塘${pondIdFromDetail || i}`;
                  pondInfo.userName = "未知负责人";
                  pondInfo.address = "未知地址";
                }
              } else {
                pondInfo.pondName = `未知虾塘${i}`;
                pondInfo.userName = "未知负责人";
                pondInfo.address = "未知地址";
              }

              // 标准化坐标格式
              const validCoords = coordinates
                .map((coord) => {
                  const coordLng = parseFloat(coord.lng || coord[0] || 0);
                  const coordLat = parseFloat(coord.lat || coord[1] || 0);
                  return { lng: coordLng, lat: coordLat };
                })
                .filter((coord) => !isNaN(coord.lng) && !isNaN(coord.lat));

              const finalCoords =
                validCoords.length >= 3
                  ? validCoords
                  : [
                      { lng: lng + 0.0001, lat: lat + 0.0001 },
                      { lng: lng - 0.0001, lat: lat + 0.0001 },
                      { lng: lng, lat: lat - 0.0001 },
                    ];

              return {
                id: pondIdFromDetail || `temp_${i}`,
                coordinates: finalCoords,
                center: { lng, lat },
                ...pondInfo,
              };
            })
          );

          // 过滤null值，保留所有有效虾塘（包括当前编辑的）
          existingPonds.value = pondDataList.filter(Boolean);
          // 绘制所有虾塘
          drawExistingPonds();
        }
      } catch (error) {
        console.error("获取已有虾塘区域失败:", error);
      }
    };

    // 绘制已有虾塘区域
    const drawExistingPonds = () => {
      if (!mapInstance || !window.T) return;

      // 清除现有虾塘元素
      existingPolygons.forEach((element) => {
        try {
          if (element._cleanup) element._cleanup();
          mapInstance.removeOverLay(element);
        } catch (e) {
          console.warn("移除现有元素失败:", e);
        }
      });
      existingPolygons = [];

      // 遍历所有虾塘，绘制时跳过当前编辑的虾塘
      existingPonds.value.forEach((pond, index) => {
        if (!pond || !pond.coordinates || !Array.isArray(pond.coordinates))
          return;

        // 核心修改：编辑模式下跳过当前编辑的虾塘（仅不渲染，数据仍保留）
        if (
          props.isEditMode &&
          props.pondId &&
          pond.id.toString() === props.pondId.toString()
        ) {
          return; // 跳过当前编辑虾塘的渲染
        }

        const validCoords = pond.coordinates.filter(
          (coord) => coord && !isNaN(coord.lng) && !isNaN(coord.lat)
        );

        if (validCoords.length < 3) return;

        try {
          if (zoomLevel.value > 15) {
            // 绘制多边形（原有逻辑保留）
            const path = validCoords.map(
              (coord) => new window.T.LngLat(coord.lng, coord.lat)
            );
            const polygon = new window.T.Polygon(path, {
              color: "#f56c6c",
              weight: 2,
              fillColor: "#f56c6c",
              fillOpacity: 0.2,
            });
            polygon.pondId = pond.id;

            // 悬浮提示框逻辑（原有保留）
            const mapContainer = document.getElementById("tdtMap");
            const tooltip = document.createElement("div");
            tooltip.style.cssText = `
              position: absolute;
              background: rgba(255, 255, 255, 0.95);
              border: 1px solid #e8e8e8;
              border-radius: 4px;
              padding: 8px 12px;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
              font-size: 12px;
              z-index: 9999;
              display: none;
              pointer-events: none;
              white-space: nowrap;
            `;
            tooltip.innerHTML = `
              <div style="font-weight: bold; margin-bottom: 4px;">${
                pond.pondName
              }</div>
              <div>负责人：${pond.userName}</div>
              <div>面积：${pond.pondArea.toFixed(2)} 平方米</div>
              <div>水深：${pond.pondDepth.toFixed(2)} 米</div>
              <div>地址：${pond.address}</div>
            `;
            mapContainer.appendChild(tooltip);

            // 鼠标事件（原有保留）
            const handlePolygonMouseOver = (e) => {
              const { clientX, clientY } = e.event || window.event;
              const mapRect = mapContainer.getBoundingClientRect();
              tooltip.style.left = `${clientX - mapRect.left + 10}px`;
              tooltip.style.top = `${clientY - mapRect.top + 10}px`;
              tooltip.style.display = "block";
            };
            const handlePolygonMouseOut = () => {
              tooltip.style.display = "none";
            };
            const handlePolygonMouseMove = (e) => {
              const { clientX, clientY } = e.event || window.event;
              const mapRect = mapContainer.getBoundingClientRect();
              tooltip.style.left = `${clientX - mapRect.left + 10}px`;
              tooltip.style.top = `${clientY - mapRect.top + 10}px`;
            };

            polygon.addEventListener("mouseover", handlePolygonMouseOver);
            polygon.addEventListener("mouseout", handlePolygonMouseOut);
            polygon.addEventListener("mousemove", handlePolygonMouseMove);

            polygon._cleanup = () => {
              tooltip.remove();
              polygon.removeEventListener("mouseover", handlePolygonMouseOver);
              polygon.removeEventListener("mouseout", handlePolygonMouseOut);
              polygon.removeEventListener("mousemove", handlePolygonMouseMove);
            };

            mapInstance.addOverLay(polygon);
            existingPolygons.push(polygon);
          } else {
            // 绘制中心点标记
            const centerPoint =
              pond.center || calculateCenterPoint(validCoords);
            addPondCenterMarker(centerPoint, pond.id, index, pond);
          }
        } catch (error) {
          console.error(`绘制虾塘ID ${pond.id || index} 失败:`, error);
        }
      });
    };

    // 防抖工具函数
    const debounce = (fn, delay = 300) => {
      let timer = null;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          fn.apply(this, args);
        }, delay);
      };
    };

    // 虾塘中心点标记方法
    const addPondCenterMarker = (centerPoint, pondId, index, pondInfo) => {
      try {
        const markerIcon = new window.T.Icon({
          iconUrl: "https://webapi.amap.com/theme/v1.3/markers/n/mark_r.png",
          iconSize: new window.T.Point(20, 20),
          iconAnchor: new window.T.Point(10, 10),
        });

        const marker = new window.T.Marker(
          new window.T.LngLat(centerPoint.lng, centerPoint.lat),
          {
            icon: markerIcon,
            id: `marker_${pondId || index}`,
          }
        );

        // 创建固定显示的虾塘名称标签
        const nameLabel = document.createElement("div");
        nameLabel.style.cssText = `
          position: absolute;
          background: rgba(255, 255, 255, 0.8);
          border: 1px solid #e8e8e8;
          border-radius: 2px;
          padding: 2px 6px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          font-size: 12px;
          z-index: 9998;
          pointer-events: none; /* 避免遮挡地图交互 */
          white-space: nowrap;
          color: #333;
          font-weight: 500;
          display: block; /* 初始显示 */
        `;
        nameLabel.innerText = pondInfo.pondName;
        const mapContainer = document.getElementById("tdtMap");
        mapContainer.appendChild(nameLabel);

        // 计算名称标签的位置（标记右上角偏移）
        const updateNameLabelPos = () => {
          if (!mapInstance) return;
          // 先显示标签，再更新位置
          nameLabel.style.display = "block";
          const pixel = mapInstance.lngLatToContainerPoint(
            new window.T.LngLat(centerPoint.lng, centerPoint.lat)
          );
          nameLabel.style.left = `${pixel.x + 15}px`;
          nameLabel.style.top = `${pixel.y - 10}px`;
        };

        // 防抖更新标签位置/显示
        const debouncedUpdateLabel = debounce(updateNameLabelPos, 300);

        // 监听地图开始移动/缩放，隐藏标签
        const hideLabelOnMapMove = () => {
          nameLabel.style.display = "none";
        };

        // 初始化位置
        updateNameLabelPos();

        // 绑定事件：开始移动/缩放时隐藏，结束后防抖更新显示
        mapInstance.addEventListener("movestart", hideLabelOnMapMove);
        mapInstance.addEventListener("zoomstart", hideLabelOnMapMove);
        mapInstance.addEventListener("moveend", debouncedUpdateLabel);
        mapInstance.addEventListener("zoomend", debouncedUpdateLabel);

        // 悬浮提示框
        const tooltip = document.createElement("div");
        tooltip.style.cssText = `
          position: absolute;
          background: rgba(255, 255, 255, 0.95);
          border: 1px solid #e8e8e8;
          border-radius: 4px;
          padding: 8px 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          font-size: 12px;
          z-index: 9999;
          display: none;
          pointer-events: none;
          white-space: nowrap;
        `;
        tooltip.innerHTML = `
          <div style="font-weight: bold; margin-bottom: 4px;">${
            pondInfo.pondName
          }</div>
          <div>负责人：${pondInfo.userName}</div>
          <div>面积：${pondInfo.pondArea.toFixed(2)} 平方米</div>
          <div>水深：${pondInfo.pondDepth.toFixed(2)} 米</div>
          <div>地址：${pondInfo.address}</div>
        `;
        mapContainer.appendChild(tooltip);

        // 悬浮提示框事件
        marker.addEventListener("mouseover", (e) => {
          const { clientX, clientY } = e.event || window.event;
          const mapRect = mapContainer.getBoundingClientRect();
          const mapX = clientX - mapRect.left;
          const mapY = clientY - mapRect.top;
          tooltip.style.left = `${mapX + 10}px`;
          tooltip.style.top = `${mapY + 10}px`;
          tooltip.style.display = "block";
        });

        marker.addEventListener("mouseout", () => {
          tooltip.style.display = "none";
        });

        marker.addEventListener("mousemove", (e) => {
          const { clientX, clientY } = e.event || window.event;
          const mapRect = mapContainer.getBoundingClientRect();
          const mapX = clientX - mapRect.left;
          const mapY = clientY - mapRect.top;
          tooltip.style.left = `${mapX + 10}px`;
          tooltip.style.top = `${mapY + 10}px`;
        });

        // 点击事件
        marker.addEventListener("click", () => {
          if (mapInstance && centerPoint) {
            const targetLngLat = new window.T.LngLat(
              centerPoint.lng,
              centerPoint.lat
            );
            mapInstance.centerAndZoom(targetLngLat, 18);
            mapCenter.value = [centerPoint.lng, centerPoint.lat];
            zoomLevel.value = 18;
            showFullArea.value = true;

            clearTimeout(window.pondDrawTimer);
            window.pondDrawTimer = setTimeout(() => {
              drawExistingPonds();
              mapInstance.panTo(targetLngLat);
            }, 300);

            ElMessage.success(`已定位到${pondInfo.pondName}`);
          }
        });

        // 清理逻辑
        marker._cleanup = () => {
          // 移除名称标签
          if (marker._nameLabel) {
            marker._nameLabel.remove();
          }
          // 移除悬浮提示框
          if (marker._tooltip) {
            marker._tooltip.remove();
          }
          // 移除地图事件监听（关键：避免内存泄漏）
          mapInstance.removeEventListener("movestart", hideLabelOnMapMove);
          mapInstance.removeEventListener("zoomstart", hideLabelOnMapMove);
          mapInstance.removeEventListener("moveend", debouncedUpdateLabel);
          mapInstance.removeEventListener("zoomend", debouncedUpdateLabel);
        };

        // 关联标签到标记
        marker._nameLabel = nameLabel;
        marker._tooltip = tooltip;

        existingPolygons.push(marker);
        marker.addTo(mapInstance);
      } catch (error) {
        console.error(`添加虾塘[${index}]中心点标记失败:`, error);
      }
    };

    // 计算中心点
    const calculateCenterPoint = (coordinates) => {
      if (!coordinates || coordinates.length === 0) return { lng: 0, lat: 0 };

      // 使用所有点的平均值计算几何中心
      const total = coordinates.reduce(
        (sum, point) => {
          sum.lng += point.lng;
          sum.lat += point.lat;
          return sum;
        },
        { lng: 0, lat: 0 }
      );

      return {
        lng: parseFloat((total.lng / coordinates.length).toFixed(6)),
        lat: parseFloat((total.lat / coordinates.length).toFixed(6)),
      };
    };

    // 切换绘制模式
    const toggleDrawingMode = () => {
      if (isDrawingMode.value) {
        exitDrawingMode();
      } else {
        startDrawingMode();
      }
    };

    // 开始绘制模式
    const startDrawingMode = () => {
      clearDrawing();
      isNewDrawing.value = true; // 标记新绘制
      isDrawingMode.value = true;
      drawingPoints.value = [];
      isAreaOverlapping.value = false;

      if (zoomLevel.value <= 15) {
        mapInstance.centerAndZoom(new window.T.LngLat(...mapCenter.value), 17);
        zoomLevel.value = 17;
      }

      // 清除之前的临时线
      if (tempPolyline) {
        try {
          mapInstance.removeOverLay(tempPolyline);
        } catch (e) {
          console.warn("移除临时线失败:", e);
        }
        tempPolyline = null;
      }

      // 更改地图光标
      if (mapInstance) {
        try {
          mapInstance.getContainer().style.cursor = "crosshair";
        } catch (e) {
          console.warn("设置光标样式失败:", e);
        }
      }

      ElMessage.success("绘制模式已开启，点击地图添加顶点");
    };

    // 退出绘制模式
    const exitDrawingMode = () => {
      isDrawingMode.value = false;

      // 清除临时线
      if (tempPolyline) {
        try {
          clearDrawing();
        } catch (e) {
          console.warn("移除临时线失败:", e);
        }
        tempPolyline = null;
      }

      // 恢复光标
      if (mapInstance) {
        try {
          mapInstance.getContainer().style.cursor = "";
        } catch (e) {
          console.warn("恢复光标样式失败:", e);
        }
      }
    };

    // 完成绘制
    const completeDrawing = () => {
      if (drawingPoints.value.length < 3) {
        ElMessage.warning("至少需要3个顶点才能完成绘制");
        return;
      }

      // 清除临时线
      if (tempPolyline) {
        try {
          mapInstance.removeOverLay(tempPolyline);
        } catch (e) {
          console.warn("移除临时线失败:", e);
        }
        tempPolyline = null;
      }

      // 设置最终坐标
      areaCoords.value = [...drawingPoints.value];

      // 检查是否与已有区域重叠
      isAreaOverlapping.value = checkAreaOverlap(areaCoords.value);

      // 计算中心点
      centerPoint.value = calculateCenterPoint(areaCoords.value);

      // 绘制最终多边形（根据重叠状态改变颜色）
      try {
        const points = areaCoords.value.map(
          (p) => new window.T.LngLat(p.lng, p.lat)
        );

        // 清除之前的多边形
        if (currentPolygon) {
          mapInstance.removeOverLay(currentPolygon);
        }

        // 如果重叠，使用红色高亮显示
        const polygonColor = isAreaOverlapping.value ? "#ff4d4f" : "#1890ff";

        currentPolygon = new window.T.Polygon(points, {
          color: polygonColor,
          weight: 3,
          fillColor: polygonColor,
          fillOpacity: isAreaOverlapping.value ? 0.3 : 0.2,
        });
        mapInstance.addOverLay(currentPolygon);
      } catch (error) {
        console.error("绘制最终多边形失败:", error);
        ElMessage.error("绘制区域失败，请重试");
        return;
      }

      // 计算面积
      calculateArea();
      isNewDrawing.value = true; // 标记为新绘制

      // 退出绘制模式
      exitDrawingMode();

      if (isAreaOverlapping.value) {
        ElMessage.warning("当前框选区域与已有虾塘区域重叠，请重新框选！");
        setTimeout(() => {
          clearDrawing();
          ElMessage.info("已自动清除重叠区域，请重新框选");
        }, 3000);
      } else {
        ElMessage.success(`区域绘制完成，共${areaCoords.value.length}个顶点`);
      }
    };

    // 检查区域是否重叠（核心算法）
    const checkAreaOverlap = (newAreaCoords) => {
      if (!existingPonds.value || existingPonds.value.length === 0) {
        return false;
      }

      // 遍历所有已有虾塘区域（排除当前编辑虾塘自身）
      for (const pond of existingPonds.value) {
        // 跳过当前编辑的虾塘（避免和自身重叠）
        if (
          props.isEditMode &&
          props.pondId &&
          pond.id.toString() === props.pondId.toString()
        ) {
          continue;
        }
        if (!pond.coordinates || pond.coordinates.length < 3) continue;

        // 检查新区域是否与当前虾塘区域重叠
        if (polygonsIntersect(newAreaCoords, pond.coordinates)) {
          return true;
        }
      }

      return false;
    };

    // 判断两个多边形是否相交
    const polygonsIntersect = (poly1, poly2) => {
      // 检查poly1的任意边是否与poly2的任意边相交
      for (let i = 0; i < poly1.length; i++) {
        const p1a = poly1[i];
        const p1b = poly1[(i + 1) % poly1.length];

        for (let j = 0; j < poly2.length; j++) {
          const p2a = poly2[j];
          const p2b = poly2[(j + 1) % poly2.length];

          if (linesIntersect(p1a, p1b, p2a, p2b)) {
            return true;
          }
        }
      }

      // 检查poly1的任意顶点是否在poly2内部
      if (isPointInPolygon(poly1[0], poly2)) {
        return true;
      }

      // 检查poly2的任意顶点是否在poly1内部
      return isPointInPolygon(poly2[0], poly1);
    };

    // 判断两条线段是否相交
    const linesIntersect = (p1, p2, p3, p4) => {
      const ccw = (A, B, C) => {
        return (
          (C.lat - A.lat) * (B.lng - A.lng) > (B.lat - A.lat) * (C.lng - A.lng)
        );
      };

      return (
        ccw(p1, p3, p4) !== ccw(p2, p3, p4) &&
        ccw(p1, p2, p3) !== ccw(p1, p2, p4)
      );
    };

    // 判断点是否在多边形内部（射线法）
    const isPointInPolygon = (point, polygon) => {
      let inside = false;

      for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].lng,
          yi = polygon[i].lat;
        const xj = polygon[j].lng,
          yj = polygon[j].lat;

        const intersect =
          yi > point.lat !== yj > point.lat &&
          point.lng < ((xj - xi) * (point.lat - yi)) / (yj - yi) + xi;

        if (intersect) inside = !inside;
      }

      return inside;
    };

    // 计算面积
    const calculateArea = () => {
      if (!areaCoords.value || areaCoords.value.length < 3) return;

      try {
        const points = areaCoords.value.map(
          (coord) => new window.T.LngLat(coord.lng, coord.lat)
        );

        // 使用天地图几何工具计算面积
        if (
          window.T &&
          window.T.GeometryUtil &&
          window.T.GeometryUtil.areaOfPolygon
        ) {
          areaArea.value = window.T.GeometryUtil.areaOfPolygon(points);
        } else {
          // 降级方案：使用更精确的球面面积计算
          areaArea.value = calculateGeodesicArea(areaCoords.value);
        }
      } catch (error) {
        console.error("计算面积失败:", error);
        areaArea.value = null;
      }
    };

    // 更精确的球面面积计算
    const calculateGeodesicArea = (coords) => {
      const earthRadius = 6378137; // 地球半径（米）
      let area = 0;
      const len = coords.length;

      for (let i = 0; i < len; i++) {
        const j = (i + 1) % len;
        const lat1 = (coords[i].lat * Math.PI) / 180;
        const lon1 = (coords[i].lng * Math.PI) / 180;
        const lat2 = (coords[j].lat * Math.PI) / 180;
        const lon2 = (coords[j].lng * Math.PI) / 180;

        area += (lon2 - lon1) * (2 + Math.sin(lat1) + Math.sin(lat2));
      }

      area = Math.abs((area * earthRadius * earthRadius) / 2);
      return area;
    };

    // 清除绘制
    const clearDrawing = () => {
      // 清除标记
      markers.forEach((marker) => {
        try {
          mapInstance.removeOverLay(marker);
        } catch (e) {
          console.warn("移除标记失败:", e);
        }
      });
      markers = [];

      // 清除多边形和临时线
      if (currentPolygon) {
        try {
          mapInstance.removeOverLay(currentPolygon);
        } catch (e) {
          console.warn("移除多边形失败:", e);
        }
        currentPolygon = null;
      }

      if (tempPolyline) {
        try {
          mapInstance.removeOverLay(tempPolyline);
        } catch (e) {
          console.warn("移除临时线失败:", e);
        }
        tempPolyline = null;
      }

      // 重置状态
      drawingPoints.value = [];
      areaCoords.value = [];
      areaArea.value = null;
      centerPoint.value = null;
      isAreaOverlapping.value = false;
      isNewDrawing.value = false; // 重置新绘制标记
    };

    // 关闭弹窗时的清理
    const handleClose = () => {
      clearDrawing();
      emit("close");
    };

    // 强制地图重绘（解决容器尺寸问题）
    const forceMapRedraw = () => {
      if (!mapInstance) return;

      const mapContainer = document.getElementById("tdtMap");
      if (!mapContainer) return;

      // 保存当前地图状态
      const center = mapInstance.getCenter();
      const zoom = mapInstance.getZoom();

      // 强制容器重绘
      mapContainer.style.display = "none";
      mapContainer.offsetHeight; // 触发重排
      mapContainer.style.display = "block";

      // 恢复地图状态
      mapInstance.centerAndZoom(center, zoom);
    };

    // 初始化地图：编辑模式加载原有区域
    const initMap = async () => {
      // 防止重复初始化（同时多次调用）
      if (isMapInitializing) {
        return;
      }

      isMapInitializing = true;

      try {
        if (!window.T) {
          await loadTdtScript();
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }

        if (!window.T || !window.T.Map) {
          throw new Error("天地图API加载不完整");
        }

        const mapContainer = document.getElementById("tdtMap");
        if (!mapContainer) {
          throw new Error("地图容器未找到");
        }

        // 清空地图容器
        mapContainer.innerHTML = "";

        let initLng = props.initCenter.lng;
        let initLat = props.initCenter.lat;

        // 初始化地图
        const targetLngLat = new window.T.LngLat(initLng, initLat);
        mapInstance = new window.T.Map("tdtMap");
        mapInstance.centerAndZoom(targetLngLat, zoomLevel.value);

        // 强制触发容器尺寸计算
        setTimeout(() => {
          const mapContainer = document.getElementById("tdtMap");
          if (mapContainer && mapInstance) {
            // 天地图重绘地图的方法
            forceMapRedraw();
            // 重新设置中心点和缩放级别
            mapInstance.centerAndZoom(targetLngLat, zoomLevel.value);
          }
        }, 300);

        // 确保滚轮缩放功能启用
        mapInstance.enableScrollWheelZoom();

        // 绑定事件
        bindMapEvents();
        setupMouseEvents();
        showFullArea.value = zoomLevel.value > 15;

        // 等待地图渲染
        await new Promise((resolve) => {
          mapInstance.addEventListener("load", resolve);
          setTimeout(resolve, 500);
        });

        // 无论是否编辑模式，都加载所有虾塘
        existingPonds.value = [];
        await fetchExistingPonds(); // 加载所有虾塘数据

        // 编辑模式：额外加载当前虾塘的原有区域（仅渲染自身区域，不影响其他虾塘）
        if (props.isEditMode && validPondId.value) {
          await loadEditModeArea(); // 加载当前编辑虾塘的区域
        }

        // 延迟定位
        setTimeout(() => {
          mapInstance.centerAndZoom(targetLngLat, zoomLevel.value);
        }, 200);

        // 提示信息
        if (validPondId.value) {
          if (props.isEditMode) {
            ElMessage.success(
              hasExistingArea.value
                ? "地图加载完成，可删除原有区域后重新框选"
                : '地图加载完成，点击"开始框选"进行区域绘制'
            );
          } else {
            ElMessage.success('地图加载完成，点击"开始框选"进行区域绘制');
          }
        }

        isMapInitializing = false; // 初始化完成
      } catch (error) {
        console.error("地图初始化失败:", error);
        ElMessage.error(`地图初始化失败: ${error.message}`);
        isMapInitializing = false; // 出错也要重置标志
      }
    };

    // 加载编辑模式下的当前虾塘区域
    const loadEditModeArea = async () => {
      try {
        const result = await getPondCoordinatesById(props.pondId);
        if (result.code === 0 && result.data && result.data.coordinates) {
          const coords = JSON.parse(result.data.coordinates);
          if (Array.isArray(coords) && coords.length >= 3) {
            editModeArea.value = coords;
            areaCoords.value = [...coords];
            // 绘制原有区域
            drawEditModeArea();
            // 计算中心点和面积
            centerPoint.value = calculateCenterPoint(coords);
            calculateArea();
            // 检查重叠
            isAreaOverlapping.value = checkAreaOverlap(coords);
            isNewDrawing.value = false; // 原有区域，非新绘制
          }
        }
        isEditAreaLoaded.value = true;
      } catch (error) {
        console.error("加载编辑区域失败:", error);
        isEditAreaLoaded.value = true;
        isNewDrawing.value = false;
        ElMessage.warning("未找到原有区域，可直接绘制新区域");
      }
    };

    // 绘制编辑模式下的原有区域
    const drawEditModeArea = () => {
      if (!mapInstance || editModeArea.value.length < 3) return;

      try {
        const points = editModeArea.value.map(
          (p) => new window.T.LngLat(p.lng, p.lat)
        );

        // 清除原有多边形
        if (currentPolygon) {
          mapInstance.removeOverLay(currentPolygon);
        }

        currentPolygon = new window.T.Polygon(points, {
          color: "#52c41a",
          weight: 3,
          fillColor: "#52c41a",
          fillOpacity: 0.2,
        });
        mapInstance.addOverLay(currentPolygon);

        // 添加顶点标记
        editModeArea.value.forEach((point) => {
          const lnglat = new window.T.LngLat(point.lng, point.lat);
          addMarker(lnglat);
        });
      } catch (error) {
        console.error("绘制编辑区域失败:", error);
      }
    };

    // 删除原有区域
    const deleteExistingArea = async () => {
      try {
        await ElMessageBox.confirm(
          "确定要删除原有虾塘区域吗？删除后可重新绘制",
          "确认删除",
          {
            confirmButtonText: "确定",
            cancelButtonText: "取消",
            type: "warning",
          }
        );

        // 调用删除接口
        const result = await deletePondCoordinatesById(props.pondId);
        if (result.code === 0) {
          // 清除地图上的原有区域
          clearAllMapElements();
          editModeArea.value = [];
          areaCoords.value = [];
          areaArea.value = null;
          centerPoint.value = null;
          isAreaOverlapping.value = false;
          isNewDrawing.value = false;

          ElMessage.success("原有区域已删除，可重新绘制");
          emit("delete-success");
        } else {
          ElMessage.error(result.message || "删除失败");
        }
      } catch (error) {
        if (error !== "cancel") {
          console.error("删除区域失败:", error);
          ElMessage.error("删除失败，请重试");
        }
      }
    };

    // 重置编辑模式下的绘制
    const resetEditDrawing = () => {
      clearDrawing();
      // 如果有原有区域，重新绘制
      if (editModeArea.value.length > 0) {
        drawEditModeArea();
        areaCoords.value = [...editModeArea.value];
        calculateArea();
        isNewDrawing.value = false; // 重置为原有区域，非新绘制
      }
    };

    // 保存区域经纬度
    const handleSaveArea = async () => {
      if (!validPondId.value) {
        ElMessage.error("虾塘ID无效，请选择有效的虾塘");
        emit("save-fail", "虾塘ID无效");
        return;
      }

      if (isAreaOverlapping.value) {
        ElMessage.warning("当前区域与已有虾塘重叠，无法保存！");
        emit("save-fail", "区域重叠");
        return;
      }

      if (!areaCoords.value || areaCoords.value.length < 3) {
        ElMessage.warning("请先框选有效区域（至少3个顶点）");
        emit("save-fail", "区域无效");
        return;
      }

      try {
        const formattedCoords = areaCoords.value.map((point) => ({
          lng: parseFloat(point.lng.toFixed(6)),
          lat: parseFloat(point.lat.toFixed(6)),
        }));

        const centerPoint = calculateCenterPoint(formattedCoords);
        const centerJson = JSON.stringify(centerPoint);

        const response = await savePondCoordinates(props.pondId, {
          coordinatesData: formattedCoords,
          central: centerJson,
          area: areaArea.value,
          pointCount: areaCoords.value.length,
        });

        if (response && response.code === 0) {
          ElMessage.success(props.isEditMode ? "区域更新成功" : "区域保存成功");
          emit("save-success", response.data);
          clearDrawing();
          emit("close");
        } else {
          ElMessage.error((response && response.message) || "保存失败");
          emit("save-fail", (response && response.message) || "保存失败");
        }
      } catch (error) {
        console.error("保存区域失败:", error);
        const errorMsg = `保存失败: ${error.message || "请重试"}`;
        ElMessage.error(errorMsg);
        emit("save-fail", errorMsg);
      }
    };

    // 清除编辑展示的区域
    const resetMapState = () => {
      // 清除所有地图元素
      clearAllMapElements();

      // 重置所有状态
      isDrawingMode.value = false;
      drawingPoints.value = [];
      areaCoords.value = [];
      areaArea.value = null;
      centerPoint.value = null;
      mousePosition.value = null;
      existingPonds.value = [];
      isAreaOverlapping.value = false;
      showFullArea.value = false;
      isNewDrawing.value = false;

      // 重置编辑模式相关状态
      editModeArea.value = [];
      isEditAreaLoaded.value = false;

      // 重置地图配置
      mapCenter.value = [props.initCenter.lng, props.initCenter.lat];
      zoomLevel.value = 15;
      enableScrollWheel.value = true;

      // 清理所有标记、多边形等
      markers = [];
      existingPolygons = [];
      currentPolygon = null;
      tempPolyline = null;

      console.log("地图状态已重置");
    };

    // 监听编辑模式变化 - 不再重新初始化地图，只重新加载数据
    watch([() => props.isEditMode, () => props.pondId], async () => {
      if (props.isEditMode && validPondId.value && mapInstance) {
        // 地图已存在时，只重新加载编辑区域数据
        await loadEditModeArea();
      }
    });

    watch(
      () => props.visible,
      (newVisible) => {
        if (newVisible && mapInstance) {
          setTimeout(() => {
            // 使用新的重绘方法
            forceMapRedraw();
          }, 300);
        }
      }
    );

    const resetMapView = () => {
      if (mapInstance) {
        // 退出绘制模式
        exitDrawingMode();
        // 重置到传入的初始中心点
        const center = new window.T.LngLat(
          props.initCenter.lng,
          props.initCenter.lat
        );
        mapInstance.centerAndZoom(center, 15);
        // 更新组件内部状态
        mapCenter.value = [props.initCenter.lng, props.initCenter.lat];
        zoomLevel.value = 15;
      }
    };

    watch(zoomLevel, (newZoom) => {
      // 根据缩放级别决定显示模式
      const shouldShowFullArea = newZoom > 15;

      if (shouldShowFullArea !== showFullArea.value) {
        showFullArea.value = shouldShowFullArea;
        // 重新绘制现有虾塘
        drawExistingPonds();
      }
    });

    // 监听弹窗显示状态
    watch(
      () => props.initCenter,
      (newCenter) => {
        if (mapInstance && newCenter.lng && newCenter.lat) {
          // 更新 mapCenter 状态
          mapCenter.value = [newCenter.lng, newCenter.lat];
          // 同步地图中心点
          mapInstance.centerAndZoom(
            new window.T.LngLat(newCenter.lng, newCenter.lat),
            zoomLevel.value
          );
          // 中心点变化后重新绘制已有虾塘
          setTimeout(() => {
            drawExistingPonds();
          }, 200);

          // 如果是编辑模式且有原有区域，重新绘制编辑区域
          if (props.isEditMode && editModeArea.value.length > 0) {
            setTimeout(() => {
              drawEditModeArea();
            }, 300);
          }
        }
      },
      { immediate: true, deep: true }
    );

    watch(
      () => props.refreshFlag,
      async (newVal) => {
        if (newVal > 0 && mapInstance) {
          await fetchExistingPonds();
          drawExistingPonds();
        }
      },
      { immediate: false }
    );

    onMounted(() => {
      // 独立页面模式下直接初始化地图
      // 弹窗模式下由父组件通过 @opened 事件调用 initMap 方法触发
      if (props.isStandalone) {
        initMap();
      }
    });

    return {
      mapCenter,
      zoomLevel,
      enableScrollWheel,
      areaCoords,
      areaArea,
      centerPoint,
      mousePosition,
      isDrawingMode,
      drawingPoints,
      hasDrawingData,
      hasValidArea,
      validPondId,
      isAreaOverlapping,
      toggleDrawingMode,
      completeDrawing,
      clearDrawing,
      handleSaveArea,
      handleClose,
      showFullArea,
      exitDrawingMode,
      resetMapView,
      fetchExistingPonds,
      deleteExistingArea,
      resetEditDrawing,
      resetMapState,
      canResetEditDrawing,
      initMap,
    };
  },
};
</script>

<style scoped>
.amap-container {
  position: relative;
  width: 100%;
  height: 600px;
}

.map-wrapper {
  width: 100%;
  height: 100%;
}

.map-container {
  width: 100%;
  height: 100%;
}

.control-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-width: 140px;
  position: absolute;
  z-index: 9999 !important;
  top: 10px;
  left: 10px;
}

.control-btn {
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background-color: #fff;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 12px;
}

.control-btn.active,
.control-btn:hover {
  border-color: #1890ff;
  background-color: #1890ff;
  color: #fff;
}

.clear-btn {
  border-color: #f56c6c;
  background-color: #f56c6c;
  color: #fff;
}

.clear-btn:hover {
  border-color: #f78989;
  background-color: #f78989;
}

/* 删除按钮样式 */
.control-btn.delete-btn {
  border-color: #ff4d4f;
  background-color: #ff4d4f;
  color: #fff;
}

.control-btn.delete-btn:hover {
  border-color: #ff7875;
  background-color: #ff7875;
}

.save-btn {
  border-color: #1890ff;
  background-color: #1890ff;
  color: #fff;
}

.save-btn:hover {
  border-color: #40a9ff;
  background-color: #40a9ff;
}

.control-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 状态提示 */
.status-tip {
  position: absolute;
  z-index: 1000;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 12px 16px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(255, 255, 255, 0.95);
  font-size: 14px;
  border: 1px solid #e8e8e8;
  text-align: center;
  max-width: 400px;
}

.status-tip.drawing {
  background-color: #f6ffed;
  border-color: #b7eb8f;
  color: #52c41a;
  top: 70px;
}

.status-tip.error {
  background-color: #fff2f0;
  border-color: #ffccc7;
  color: #ff4d4f;
  top: 10px;
}

.status-tip.error.has-zoom-tip {
  top: 70px;
}

.status-tip small {
  color: #666;
  font-size: 12px;
}

/* 缩放提示样式 */
.zoom-tip {
  position: absolute;
  z-index: 1000;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 16px;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 4px;
  font-size: 12px;
  color: #d48806;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 400px;
}

/* 区域信息面板 */
.area-info {
  position: absolute;
  z-index: 1000;
  top: 70px;
  right: 10px;
  padding: 15px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: 300px;
  background-color: white;
  max-height: 70vh;
  overflow-y: auto;
}

.area-info h3 {
  margin-top: 0;
  margin-bottom: 12px;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
  color: #2c3e50;
  font-size: 16px;
}

.info-section {
  font-size: 14px;
}

.center-point {
  margin: 8px 0;
  padding: 8px;
  background: #f6ffed;
  border-radius: 4px;
  border: 1px solid #b7eb8f;
}

.center-point .coord {
  margin: 2px 0;
  font-family: monospace;
  font-size: 12px;
  color: #389e0d;
}

.coordinates-preview {
  margin-top: 8px;
}

.coords-list {
  margin-top: 4px;
  max-height: 150px;
  overflow-y: auto;
}

.coord-item {
  padding: 4px 0;
  border-bottom: 1px solid #f0f0f0;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  font-family: monospace;
  font-size: 12px;
}

.coord-item:last-child {
  border-bottom: none;
}

/* 鼠标位置信息 */
.mouse-position {
  position: absolute;
  z-index: 1000;
  bottom: 40px;
  left: 10px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 4px;
  font-size: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  border: 1px solid #d9d9d9;
}

.drawing-hint {
  color: #1890ff;
  font-weight: 500;
}

:deep(.TIcon) {
  background: transparent !important;
  border: none !important;
}

:deep(.TIcon img) {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

/* 确保天地图容器可以正常拖动 */
#tdtMap {
  touch-action: pan-x pan-y;
  user-select: none;
  -webkit-user-select: none;
}

#tdtMap * {
  touch-action: pan-x pan-y;
}
</style>
